import React from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
// Styles
import styles from './dropdown.module.scss';
// Components
import { ScrollView } from '../..';
// Interfaces
import { IItem, IMultiSelectDropdownItem } from '../../../interfaces/general.interfaces';
// Assets
import images from '../../../assets/images/images';
// Helpers
import { isTouchScreen } from '../../../helpers/browser';
import KeyboardHandler from '../../../helpers/keyboardHandler';
import { SpriteImage } from '../../SpriteImage/SpriteImage';
// Config
import { staticConfig } from '../../../config/static';
import { Image } from '../..';
import { InputError } from '..';
import { closePopupOFocusOut } from '../../../helpers/general';
import { MailboxAccessType } from '../../../enums';

const dropdownListMaxHeight = 230;
const dropdownHeight = 40;

interface IDropdownProps extends WrappedComponentProps {
    autofocus?: boolean;
    selected?: any;
    initial?: any;
    clearable?: boolean;
    list: Array<any>;
    className?: string;
    displayKey?: string;
    placeholder?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    translateData?: boolean;
    textColor?: string;
    minListWidth?: number;
    required?: boolean;
    touched?: boolean;
    disabled?: boolean;
    multiSelect?: boolean;
    onSelect: (item: any) => boolean | void;
    onError?: () => void;
    onBlur?: () => void;
    onRoleList?: boolean;
    selectedTrusteeFromList?: any;
}

interface IDropdownState {
    listShown: boolean;
    selected?: any;
    touched: boolean;
    cursor: number;
    multiSelectList?: IMultiSelectDropdownItem[];
    searchvalue?: string;
    listItems?: any;
}

class _Dropdown extends React.Component<IDropdownProps, IDropdownState> {
    public readonly checkAllTextKey = 'CHECK_ALL';
    listItemHeight = dropdownHeight;
    itemsListRef = React.createRef<Scrollbars>();
    dropdownRef = React.createRef<HTMLDivElement>();
    keyboardHandlerInstance = new KeyboardHandler();

    constructor(props: IDropdownProps) {
        super(props);

        this.state = {
            listShown: false,
            selected: this.props.selected || this.props.initial,
            touched: false,
            cursor: this.getSelectedOrInitialItemIndex(),
            multiSelectList: this.props.multiSelect ? this.props.list : null,
            searchvalue: '',
            listItems: [],
        };
    }

    componentDidMount() {
        this.setupKeyboardHandler();
        if (this.props.autofocus) {
            this.dropdownRef.current.focus();
        }

        const newList = this.renderListItems(this.props.textColor);
        this.setState({ listItems: newList });
    }

    componentDidUpdate(prevProps: IDropdownProps, prevState: IDropdownState) {
        if (this.props.touched !== prevProps.touched) {
            this.setState({ touched: this.props.touched });
        }

        if (this.props.selected !== undefined && this.props.selected !== prevProps.selected) {
            this.setState({
                selected: this.props.selected,
                touched: prevProps.touched && this.props.touched,
            });
        }

        if (this.props.required !== prevProps.required || this.props.touched !== prevProps.touched) {
            if (this.props.onError && this.checkErrors()) {
                this.props.onError();
            }
        }

        if (prevState.listShown !== this.state.listShown) {
            this.setState(
                {
                    searchvalue: '',
                },
                () => {
                    const newList = this.renderListItems(this.props.textColor, false);
                    this.setState({ listItems: newList });
                }
            );
        }
    }

    setupKeyboardHandler = () => {
        this.keyboardHandlerInstance.addHandlers({
            enter: this.onEnterKeyHandler,
            esc: () => {
                this.setState({ listShown: false });
            },
            space: this.onSpaceKeyHandler,
            up: (event) => {
                if (!this.props.onRoleList) {
                    const cursorActive = this.state.cursor - 1;
                    this.dropdownRef.current.setAttribute('aria-activedescendant', `activeItem${cursorActive}`);
                    event.altKey ? this.setState({ listShown: false }) : this.onUpKeyHandler();
                }
            },
            down: (event) => {
                if (!this.props.onRoleList) {
                    const cursorActive = this.state.cursor + 1;
                    this.dropdownRef.current.setAttribute('aria-activedescendant', `activeItem${cursorActive}`);

                    event.altKey ? this.onEnterKeyHandler(event) : this.onDownKeyHandler();
                }
            },
        });
    };

    getSelectedOrInitialItemIndex = () => {
        const { selected, initial, list } = this.props;
        const selectedItem = selected || initial;
        const itemIndex = selectedItem ? list.findIndex((el) => selectedItem.value === el.value) : 0;

        return itemIndex > 0 ? itemIndex : 0;
    };

    getModifiedMultiSelectList = (item: IMultiSelectDropdownItem, index: number) => {
        const multiSelectList = [...this.state.multiSelectList];

        if (item.value !== this.checkAllTextKey) {
            multiSelectList[0].isChecked = false;
            multiSelectList[index].isChecked = !multiSelectList[index].isChecked;
        } else {
            multiSelectList.forEach((el) => (el.isChecked = el.key === item.key ? !item.isChecked : item.isChecked));
        }

        return multiSelectList;
    };

    onSelect(item: any, event: React.MouseEvent | React.KeyboardEvent, index?: number) {
        event.stopPropagation();
        if (this.props.multiSelect) {
            const multiSelectList = this.getModifiedMultiSelectList(item, index);

            this.setState({ multiSelectList });
            this.toggleList(event, this.props.multiSelect);
            if (this.props.onSelect) this.props.onSelect(multiSelectList);
        } else {
            this.props.onSelect(item) ? this.setState({ selected: null }) : this.setState({ selected: item });
            this.toggleList(event);
        }
    }

    toggleList = (event: React.MouseEvent | React.KeyboardEvent, multiSelect?: boolean) => {
        event.stopPropagation();
        this.setState((state) => ({ listShown: multiSelect || !state.listShown, touched: true }));
    };

    shouldShowToTop = (listHeight: number) => {
        if (!this.dropdownRef.current) return false;
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        const dropdownMaxHeight = this.dropdownRef.current.offsetHeight + listHeight;
        return this.dropdownRef.current.getBoundingClientRect().top + dropdownMaxHeight > viewportHeight;
    };

    focus = (event?: React.FocusEvent) => {
        event && event.stopPropagation();
        this.setState({ listShown: false });
    };

    blur = (event?: React.FocusEvent) => {
        event && event.stopPropagation();
        this.setState({ listShown: false });
        this.props.onBlur && this.props.onBlur();
    };

    clear = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        this.setState({ selected: null });
        this.props.onSelect(null);
    };

    renderListItemTitle = (item: IItem) => {
        const { translateData, intl, displayKey } = this.props;
        return translateData
            ? intl.formatMessage({ id: displayKey ? item[displayKey] : item.key })
            : displayKey
            ? item[displayKey]
            : item.key;
    };

    renderSelectedItemText = (selectedItem: IItem) => {
        return selectedItem
            ? this.props.translateData
                ? this.props.intl.formatMessage({
                      id: this.props.displayKey ? selectedItem[this.props.displayKey] : selectedItem.key,
                  })
                : this.props.displayKey
                ? selectedItem[this.props.displayKey]
                : selectedItem.key
            : this.props.intl.formatMessage({ id: this.props.placeholder || 'SELECT' });
    };

    onMouseEnterHandler = (index: number) => {
        if (index !== this.state.cursor) {
            this.setState({ cursor: index });
        }
    };

    onMouseLeaveHandler = () => {
        this.setState({ cursor: -1 });
    };

    onSpaceKeyHandler = (event: React.KeyboardEvent) => {
        const { cursor, listShown } = this.state;
        const { multiSelect, list } = this.props;

        if (multiSelect) {
            listShown
                ? this.onSelect(list[cursor], event, cursor)
                : this.setState((state) => ({ listShown: !state.listShown }));
        } else {
            this.setState({ listShown: true });
        }
    };

    onEnterKeyHandler = (event: React.KeyboardEvent) => {
        const { cursor, listShown } = this.state;
        const { multiSelect, list } = this.props;
        const listItemDisabled = list[cursor] && list[cursor].disabled;

        if (multiSelect) {
            this.setState((state) => ({ listShown: !state.listShown }));
        } else {
            listShown && !listItemDisabled
                ? this.onSelect(list[cursor], event, cursor)
                : this.setState((state) => ({
                      listShown: !state.listShown,
                      cursor: this.getSelectedOrInitialItemIndex(),
                  }));
        }
    };

    scrollToSelected = () => {
        this.itemsListRef.current.scrollTop(this.state.cursor * this.listItemHeight);
    };

    onUpKeyHandler = () => {
        const cursor = this.state.cursor - 1;

        if (cursor < 0) return;
        this.setState({ cursor }, this.scrollToSelected);
    };

    onDownKeyHandler = () => {
        const cursor = this.state.cursor + 1;
        const itemList = this.props.multiSelect ? this.state.multiSelectList : this.props.list;

        if (cursor > itemList.length - 1) return;
        this.setState({ cursor }, this.scrollToSelected);
    };

    checkErrors = () => {
        const selected = this.state.selected || this.props.selected;
        return this.props.required && !selected;
    };

    renderListItems(textColor: string, filterChange?: boolean) {
        const filteredList = this.props.list?.filter(
            (item) =>
                item.key.toUpperCase()?.indexOf(this.state.searchvalue.toUpperCase()) > -1 ||
                item.cvrNumber?.indexOf(this.state.searchvalue) > -1
        );
        const itemList =
            filterChange && this.props.onRoleList
                ? filteredList
                : this.props.multiSelect
                ? this.state.multiSelectList
                : this.props.list;
        const notTouchScreen = !isTouchScreen();

        return itemList.map((item, index) => {
            const isHoveredEnabledItem = notTouchScreen && !item.disabled && this.state.cursor === index;
            const listItemClass = [
                styles.item,
                isHoveredEnabledItem ? styles.active : '',
                item.disabled ? styles.disabled : '',
            ].join(' ');

            return (
                <div
                    key={`activeItem${index}`}
                    id={`activeItem${index}`}
                    role="option"
                    {...(this.props.onRoleList && {
                        tabIndex: 0,
                        onKeyDown: (e) => e.key === 'Enter' && this.onSelect(item, e, index),
                    })}
                    aria-selected={item.isChecked}
                    style={{
                        borderBottom: '1px solid #E3E3E3',
                        ...(!this.props.onRoleList && { padding: '9.4px 10px' }),
                        backgroundColor:
                            this.props.selectedTrusteeFromList &&
                            this.props.selectedTrusteeFromList.find((items: any) => items.id === item.value)
                                ? '#ced4da'
                                : '',
                    }}
                    className={[listItemClass, 'd-flex align-items-center prevFocusedElem'].join(' ')}
                    onClick={(event) => this.onSelect(item, event, index)}
                    {...(notTouchScreen && {
                        onMouseEnter: () => this.onMouseEnterHandler(index),
                        onMouseLeave: this.onMouseLeaveHandler,
                    })}
                >
                    <div
                        title={this.renderListItemTitle(item)}
                        style={textColor ? { color: textColor } : {}}
                        className={[
                            this.props.multiSelect ? styles.multiSelect : '',
                            item.isChecked ? styles.checkedItem : '',
                            'text-truncate',
                        ].join(' ')}
                    >
                        <div style={this.props.onRoleList ? { fontWeight: 600, color: 'black' } : {}}>
                            {this.renderListItemText(item)}
                        </div>
                        {this.props.onRoleList && (
                            <div>
                                {item.role !== 'CITIZEN' && <>CVR : {item.cvrNumber}&nbsp;</>}
                                {item.accessType === MailboxAccessType.OWNER ? (
                                    <span>
                                        <FormattedMessage id="MY_EBOKS" />
                                        &nbsp;
                                    </span>
                                ) : item?.privileges.includes(MailboxAccessType.FULL_POWER_OF_ATTORNEY) ? (
                                    <>
                                        &#40;
                                        <FormattedMessage id="READ_WRITE_ACCESS" />
                                        &#41;
                                    </>
                                ) : (
                                    <>
                                        &#40;
                                        <FormattedMessage id="READ_ACCESS" />
                                        &#41;
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        });
    }

    renderListItemText = (item: IItem) => {
        if (item.key === this.checkAllTextKey) {
            return <FormattedMessage id={this.checkAllTextKey} />;
        } else {
            return this.props.translateData ? (
                <FormattedMessage id={this.props.displayKey ? item[this.props.displayKey] : item.key} />
            ) : this.props.displayKey ? (
                item[this.props.displayKey]
            ) : (
                item.key
            );
        }
    };

    calculateListHeight = () => {
        const listItems = this.props.multiSelect ? this.state.multiSelectList : this.props.list;
        const totalListItemsHeight = listItems.length * dropdownHeight;
        return totalListItemsHeight < dropdownListMaxHeight ? totalListItemsHeight : dropdownListMaxHeight;
    };

    onFilterChange = () => {
        const newList = this.renderListItems(this.props.textColor, true);
        this.setState({ listItems: newList });
    };

    textBoxOnChange = () => {
        this.setState(
            {
                searchvalue: '',
            },
            () => {
                const newList = this.renderListItems(this.props.textColor, true);
                this.setState({ listItems: newList });
            }
        );
    };

    public render() {
        const selected = this.state.selected || this.props.selected;
        const touched = this.state.touched || this.props.touched;
        const isErrorShown = this.checkErrors() && touched;
        const dropdownClasses = [
            styles.dropdown,
            this.props.className,
            this.props.disabled ? styles.disabled : '',
            isErrorShown ? styles.error : '',
        ].join(' ');
        const notTouchScreen = !isTouchScreen();
        const listHeight = this.calculateListHeight();
        const listStyles = {
            height: listHeight,
            maxHeight: dropdownListMaxHeight,
            top: this.shouldShowToTop(listHeight) ? -(listHeight + 2) : dropdownHeight,
        };

        const classicLogonPlace = localStorage.getItem('classicLogonPlace');
        const classicMailboxLink = staticConfig?.classicMailboxLink?.replace('{domainType}', classicLogonPlace);
        const classicLink = classicMailboxLink?.replace('/mailbox', '');

        return (
            <>
                <div
                    tabIndex={0}
                    role="listbox"
                    className={dropdownClasses}
                    // style={{ height: dropdownHeight }}
                    onBlur={!this.props.onRoleList && this.blur}
                    ref={this.dropdownRef}
                    {...(this.props.ariaLabel && {
                        'aria-label': this.props.intl.formatMessage({ id: this.props.ariaLabel }),
                    })}
                    {...(this.props.ariaLabelledBy && { 'aria-labelledby': this.props.ariaLabelledBy })}
                    {...(this.props.onRoleList
                        ? notTouchScreen && { onKeyDown: this.keyboardHandlerInstance.onPress }
                        : notTouchScreen && {
                              onKeyDown: !this.props.disabled && this.keyboardHandlerInstance.onPress,
                          })}
                    aria-activedescendant={`activeItem${this.state.cursor}`}
                >
                    <div
                        className={[styles.selected, 'd-flex align-items-center justify-content-between'].join(' ')}
                        onClick={this.toggleList}
                    >
                        <div
                            title={this.renderSelectedItemText(selected)}
                            className={[selected === null ? styles.placeholder : '', 'text-truncate'].join(' ')}
                            style={this.props.textColor ? { color: this.props.textColor } : {}}
                        >
                            <div style={this.props.onRoleList ? { fontWeight: 600, color: 'black' } : {}}>
                                {this.renderSelectedItemText(selected)}
                            </div>
                            {this.props.onRoleList && (
                                <div>
                                    {selected?.role !== 'CITIZEN' && (
                                        <span className={styles.subText}>CVR : {selected?.cvrNumber}&nbsp;</span>
                                    )}
                                    {selected?.accessType === MailboxAccessType.OWNER ? (
                                        <span className={styles.subText}>
                                            <FormattedMessage id="MY_EBOKS" />
                                            &nbsp;
                                        </span>
                                    ) : selected?.privileges.includes(MailboxAccessType.FULL_POWER_OF_ATTORNEY) ? (
                                        <span className={styles.subText}>
                                            &#40;
                                            <FormattedMessage id="READ_WRITE_ACCESS" />
                                            &#41;
                                        </span>
                                    ) : (
                                        <span className={styles.subText}>
                                            &#40;
                                            <FormattedMessage id="READ_ACCESS" />
                                            &#41;
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            {this.props.clearable && selected && (
                                <a
                                    href={staticConfig.defaultUrl}
                                    onClick={(event) => this.clear(event)}
                                    className={[styles.clearControl, 'mx-2'].join(' ')}
                                >
                                    <Image src={images.modalClose} />
                                </a>
                            )}
                            <SpriteImage
                                src={images.chevronDown}
                                className={[styles.chevron, 'ml-1'].join(' ')}
                                style={this.props.textColor ? { color: this.props.textColor } : {}}
                            />
                        </div>
                    </div>
                    <div
                        className={[styles.list, this.state.listShown ? styles.shown : ''].join(' ')}
                        style={
                            this.props.onRoleList
                                ? {}
                                : this.props.minListWidth
                                ? { minWidth: this.props.minListWidth + 'px', ...listStyles }
                                : { ...listStyles }
                        }
                    >
                        {this.props.onRoleList && (
                            <div className="filterRoleList">
                                <div className="searchInputUserRole">
                                    <input
                                        className="filterInput inputPlaceholderGrey"
                                        type="text"
                                        onKeyDown={(e) =>
                                            (e.key.includes(' ') || e.key === 'Enter') && e.stopPropagation()
                                        }
                                        onChange={(e) =>
                                            this.setState({ searchvalue: e.target.value }, () => this.onFilterChange())
                                        }
                                        placeholder={this.props.intl.formatMessage({ id: 'SEARCH_SENDER_USER' })}
                                        value={this.state.searchvalue}
                                    />
                                    <div className="clearButtonDiv">
                                        {this.state.searchvalue.length != 0 && (
                                            <button className="clearButton" onClick={this.textBoxOnChange}>
                                                x
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {this.props.onRoleList ? (
                            <>
                                <div className={styles.rolelistStyle}>{this.state.listItems}</div>
                            </>
                        ) : (
                            <ScrollView ref={this.itemsListRef}>
                                {this.renderListItems(this.props.textColor)}
                            </ScrollView>
                        )}

                        {this.props.onRoleList && (
                            <>
                                <div
                                    className="ShearedFolderUserRole"
                                    onClick={() => {
                                        window.location.href = `${classicLink}/settings/sharedfolders`;
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter')
                                            window.location.href = `${classicLink}/settings/sharedfolders`;
                                    }}
                                    onBlur={(e) => {
                                        closePopupOFocusOut(e, false) && this.focus();
                                    }}
                                    tabIndex={0}
                                    role="button"
                                >
                                    {this.props.intl.formatMessage({ id: 'MENU_SHARED_FOLDERS' })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {isErrorShown && (
                    <InputError>
                        <FormattedMessage id="REQUIRED_FIELD" />
                    </InputError>
                )}
            </>
        );
    }
}

export const Dropdown = injectIntl(_Dropdown);
